import { useIsOrgReadOnly } from '@/components/auth/claimsUtils';
import { EnhancedFormikForm } from '@/components/EnhancedFormikForm';
import { useGetOrganizationDetailsQuery, useLazyGetOrganizationDetailsQuery } from '@/services/organizations';
import { useGetWarrantyContractSkusQuery, useGetWarrantyProvidersQuery } from '@/services/warranties';
import {
  Box,
  Button,
  FormikSelectInput,
  FormikTextInput,
  Icon,
  SelectInputNative,
  TextLink,
  toast,
  Toggle,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, Formik, FormikHelpers, useField, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  EXTENDED_WARRANTY_PROGRAM_TYPES,
  OnPointDealerID,
  OrganizationStatus,
  OrganizationType,
  OrganizationTypeLabels,
  ProgramType,
  ProgramTypeLabels,
  UserPermissions,
  WARRANTY_SKU_PROGRAM_TYPES,
  WarrantyContractSkuTermMapping,
  WarrantyProvider,
} from 'types';

import usePermissions from '../../../hooks/usePermissions';
import { AddressFormFields } from '../../AddressFormFields/AddressFormFields';
import { useAuth } from '../../auth/authProvider';
import { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';
import FormSection from '../../Forms/FormSection';

import { ChangeOrganizationStatusModal } from './ChangeOrganizationStatusModal';

const AliasField = (props: any) => {
  const { values, touched, setFieldValue }: any = useFormikContext();
  const [field] = useField(props);

  useEffect(() => {
    if (props.isDisabled) return;
    if (values?.name.trim() !== '' && touched.name) {
      setFieldValue(
        props.name,
        values?.name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-'),
      );
    }
  }, [values?.name, props.name, props.isDisabled, setFieldValue, touched.alias, touched.name]);

  return (
    <>
      <Field {...field} {...props} component={FormikTextInput} />
    </>
  );
};

export type OrganizationFormSchema = yup.InferType<typeof organizationSchema>;
type SubmitHandler = (values: any, formikHelpers: FormikHelpers<any>) => void | Promise<any>;

interface OrganizationFormProps {
  initialValues: any;
  handleSubmit: SubmitHandler;
  isLoading: boolean;
  parentOptions: {
    label: string;
    value: string;
  }[];
  statusOptions: {
    label: string;
    value: string;
  }[];
  disabledFields?: string[];
  cancelButtonUrl?: string;
  disableSubmit?: boolean;
  isCreateMode?: boolean;
  accountBundlingInheritedFrom?: { alias: string; name: string };
}

const organizationSchema = yup.object().shape({
  name: yup.string().required('A friendly name is required'),
  alias: yup.string().required('An alias is required'),
  email: yup.string().email('Invalid email address').required('An email is required'),
  phoneNumber: yup.string().required('A phone number is required'),
  federalEmployerId: yup.string(),
  address1: yup.string(),
  city: yup.string(),
  state: yup.string(),
  zip: yup.string(),
  insuranceCompanyName: yup.string(),
  insuranceCompanyPhoneNumber: yup.string(),
  programTypes: yup.array().of(yup.string()).min(1),
  organizationTypes: yup.array().of(yup.string()),
  // Extended Warranty Dealer Information
  extendedWarrantyDealerInformation: yup.array().of(
    yup
      .object()
      .shape({
        warrantyProvider: yup.string(),
        dealerLicenseNumber: yup.string().required('Dealer license number is required'),
        contractSku: yup.string().notRequired(),
        contractSkuTermMappings: yup.array().of(
          yup.object().shape({
            programType: yup.string().required('Program type is required'),
            term: yup.number().notRequired(),
            contractSku: yup.string().notRequired(),
          }),
        ),
      })
      .test(
        'onpoint-sku-required',
        'OnPoint entries must have at least one contract SKU mapping or a legacy contract SKU',
        (value) => {
          if (value?.warrantyProvider !== WarrantyProvider.onPointWarranty) return true;
          return !!(value.contractSku || value.contractSkuTermMappings?.length);
        },
      ),
  ),
  accountBundlingEnabled: yup.boolean(),
});

export const OrganizationForm = ({
  initialValues,
  handleSubmit,
  isLoading,
  parentOptions,
  statusOptions,
  disabledFields = [],
  cancelButtonUrl = '/settings/organizations',
  disableSubmit,
  isCreateMode = false,
  accountBundlingInheritedFrom,
}: OrganizationFormProps) => {
  return (
    <>
      <Formik
        validationSchema={organizationSchema}
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        <EnhancedFormikForm noValidate>
          <OrganizationFormDetails
            parentOptions={parentOptions}
            statusOptions={statusOptions}
            isLoading={isLoading}
            disabledFields={disabledFields}
            cancelButtonUrl={cancelButtonUrl}
            disableSubmit={disableSubmit}
            isCreateMode={isCreateMode}
            accountBundlingInheritedFrom={accountBundlingInheritedFrom}
          />
        </EnhancedFormikForm>
      </Formik>
    </>
  );
};

type SkuData = {
  warrantyProvider: string;
  programType: string;
  term: number;
  contractSku: string;
  displayName?: string;
};

const getOnPointSkuTerms = (allSkus: SkuData[], programType: string): number[] =>
  [
    ...new Set(
      allSkus
        .filter((s) => s.warrantyProvider === WarrantyProvider.onPointWarranty && s.programType === programType)
        .map((s) => s.term),
    ),
  ].sort((a, b) => a - b);

const getOnPointProgramOptions = (programTypes: ProgramType[] = [], allSkus: SkuData[]) =>
  programTypes
    .filter((programType) => WARRANTY_SKU_PROGRAM_TYPES.has(programType))
    .filter((programType) => getOnPointSkuTerms(allSkus, programType).length > 0)
    .map((programType) => ({
      label: ProgramTypeLabels[programType],
      value: programType,
    }));

const getDefaultOnPointSkuMapping = (programTypes: ProgramType[] = [], allSkus: SkuData[]) => {
  const programType = programTypes
    .filter((type) => WARRANTY_SKU_PROGRAM_TYPES.has(type))
    .find((type) => getOnPointSkuTerms(allSkus, type).length > 0);
  if (!programType) {
    return undefined;
  }

  const term = getOnPointSkuTerms(allSkus, programType)[0];
  const contractSku =
    allSkus.find(
      (s) =>
        s.warrantyProvider === WarrantyProvider.onPointWarranty && s.programType === programType && s.term === term,
    )?.contractSku ?? '';
  return { programType, term, contractSku };
};

const getAvailableOnPointContractSkuTerms = (
  mappings: WarrantyContractSkuTermMapping[],
  programType: ProgramType,
  mappingIndex: number,
  allSkus: SkuData[],
  currentTerm?: number,
) => {
  const usedTerms = new Set(
    mappings
      .filter((mapping, index) => index !== mappingIndex && mapping.programType === programType && mapping.term)
      .map((mapping) => Number(mapping.term)),
  );

  return getOnPointSkuTerms(allSkus, programType).filter((term) => term === currentTerm || !usedTerms.has(term));
};

const getAvailableOnPointProgramOptions = (
  mappings: WarrantyContractSkuTermMapping[],
  programOptions: ReturnType<typeof getOnPointProgramOptions>,
  mappingIndex: number,
  allSkus: SkuData[],
  currentProgramType?: ProgramType,
  currentTerm?: number,
) =>
  programOptions.filter(
    (option) =>
      option.value === currentProgramType ||
      getAvailableOnPointContractSkuTerms(
        mappings,
        option.value,
        mappingIndex,
        allSkus,
        option.value === currentProgramType ? currentTerm : undefined,
      ).length > 0,
  );

const OrganizationFormDetails = ({
  isLoading,
  parentOptions,
  statusOptions,
  disabledFields = [],
  cancelButtonUrl = '/settings/organizations',
  disableSubmit = false,
  isCreateMode = false,
  accountBundlingInheritedFrom,
}: Omit<OrganizationFormProps, 'initialValues' | 'handleSubmit'>) => {
  const { claims } = useAuth();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const isNotPalmettoFinanceUserOrOrgAdmin =
    !isPalmettoFinanceUser(claims) && !permissions.includes(UserPermissions.admin);
  const [getOrganizationDetails] = useLazyGetOrganizationDetailsQuery();
  const { values, setFieldValue, isSubmitting, initialValues } = useFormikContext<any>();
  const { data: warrantyProviderResponse } = useGetWarrantyProvidersQuery({});
  const { data: allContractSkus = [] } = useGetWarrantyContractSkusQuery({});
  const activeContractSkus = useMemo(() => allContractSkus.filter((s) => s.status === 'active'), [allContractSkus]);

  const persistedStatusRef = useRef<{ label: string; value: string } | undefined>(initialValues.status);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    from: OrganizationStatus;
    to: OrganizationStatus;
    toOption: { label: string; value: string };
  } | null>(null);

  useEffect(() => {
    persistedStatusRef.current = initialValues.status;
  }, [initialValues.status]);

  const handleStatusChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newOption = event.target.value as any as { label: string; value: string };
      if (!newOption) return;
      const persistedOption = persistedStatusRef.current;
      if (persistedOption && newOption.value !== persistedOption.value) {
        setFieldValue('status', persistedOption, false);
        setPendingStatusChange({
          from: persistedOption.value as OrganizationStatus,
          to: newOption.value as OrganizationStatus,
          toOption: newOption,
        });
      } else {
        setFieldValue('status', newOption, false);
        setFieldValue('statusChangeDescription', '');
      }
    },
    [setFieldValue],
  );

  const handleStatusModalCancel = useCallback(() => {
    setPendingStatusChange(null);
    setFieldValue('statusChangeDescription', '');
  }, [setFieldValue]);

  const handleStatusModalConfirm = useCallback(
    (description: string) => {
      if (!pendingStatusChange) return;
      setFieldValue('status', pendingStatusChange.toOption);
      setFieldValue('statusChangeDescription', description);
      setPendingStatusChange(null);
    },
    [pendingStatusChange, setFieldValue],
  );
  const {
    enableWaterHeater = false,
    enableGenerator = false,
    enableConsumerSolar = false,
    enableGeothermal = false,
    accountBundling = false,
  } = useFlags();

  const isLightReachAdmin = isPalmettoFinanceUser(claims) && permissions.includes(UserPermissions.admin);

  const { data: parentOrgData } = useGetOrganizationDetailsQuery(
    {
      alias: values?.parent?.value,
    },
    { skip: !values?.parent?.value },
  );

  // A new org has no settings of its own yet, so it inherits whatever its chosen parent resolves to.
  const bundlingInheritedFrom = useMemo(() => {
    if (!isCreateMode) return accountBundlingInheritedFrom;
    if (!parentOrgData?.accountBundling?.enabled) return undefined;
    const parentBundling = parentOrgData.accountBundling;
    return parentBundling.isInherited && parentBundling.source
      ? parentBundling.source
      : { alias: parentOrgData.alias, name: parentOrgData.name };
  }, [isCreateMode, accountBundlingInheritedFrom, parentOrgData]);

  // Flags only hide types; a child org can never be offered a type its parent doesn't have.
  // With no parent (e.g. the top-level org), every type is available.
  const parentPermittedProgramTypes = parentOrgData?.programTypes || Object.values(ProgramType);

  const currentOrgProgramTypes = values.programTypes || [];

  const flaggedProgramTypes: Partial<Record<ProgramType, boolean>> = {
    [ProgramType.waterHeater]: enableWaterHeater,
    [ProgramType.generator]: enableGenerator,
    [ProgramType.consumerSolar]: enableConsumerSolar,
    [ProgramType.geothermal]: enableGeothermal,
  };

  const programTypesPermittedByParentOrgAndAnyCurrOrgProgramTypes = [
    ...new Set([...parentPermittedProgramTypes, ...currentOrgProgramTypes]),
  ].filter(
    (type: ProgramType) =>
      !Object.prototype.hasOwnProperty.call(flaggedProgramTypes, type) ||
      flaggedProgramTypes[type] ||
      currentOrgProgramTypes.includes(type),
  );

  const enabledOrganizationTypes = [OrganizationType.epc];
  if (isPalmettoFinanceUser(claims) && permissions.includes(UserPermissions.admin)) {
    enabledOrganizationTypes.push(OrganizationType.integratedPlatform);
    enabledOrganizationTypes.push(OrganizationType.materials);
  }

  const toggleProgramType = (programType: string, enabled: boolean) => {
    const programTypes = values.programTypes;
    if (programTypes) {
      if (enabled) {
        programTypes.push(programType);
        setFieldValue('programTypes', programTypes);
      } else {
        setFieldValue(
          'programTypes',
          programTypes.filter((p: string) => p !== programType),
        );
      }
    } else {
      if (enabled) {
        setFieldValue('programTypes', [programType]);
      }
    }
  };

  const isAuthorizedSignerFieldDisabled = () =>
    !isPalmettoFinanceUser(claims) ||
    !(permissions.includes(UserPermissions.lightReachSupport) || permissions.includes(UserPermissions.admin)) ||
    disabledFields.includes('authorizedSigners');
  const allowsAction = useIsOrgReadOnly();

  let warrantyProviderList: string[] = [];
  (values.programTypes as ProgramType[]).forEach((programType) => {
    const programTypeProviders = warrantyProviderResponse?.data?.find(
      (item) => item.programType === programType,
    )?.providers;

    warrantyProviderList = [...warrantyProviderList, ...(programTypeProviders?.map((p) => p.name) || [])];
  });
  const warrantyProviderOptions = warrantyProviderList.map((element) => {
    return {
      label: element,
      value: element,
    };
  });

  const programOptions = useMemo(
    () => getOnPointProgramOptions(values.programTypes, activeContractSkus),
    [values.programTypes, activeContractSkus],
  );
  const defaultMapping = useMemo(
    () => getDefaultOnPointSkuMapping(values.programTypes, activeContractSkus),
    [values.programTypes, activeContractSkus],
  );

  return (
    <>
      <FormSection
        title="Details"
        description="Organizations are used both for organization and isolation of resources"
      >
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          gap={{ base: 'lg', desktop: 'xl' }}
        >
          <Field
            type="text"
            label="Name"
            name="name"
            id="name"
            component={FormikTextInput}
            isRequired
            autoComplete="off"
            isDisabled={isSubmitting || isLoading || disabledFields.includes('name')}
          />
          <AliasField
            type="text"
            label="Alias"
            name="alias"
            id="alias"
            isRequired
            isDisabled={isSubmitting || isLoading || disabledFields.includes('alias')}
          />
        </Box>
        <Field
          label="Parent Organization"
          helpText="Members of the parent organization will be able to view and manage the new organization."
          name="parent"
          id="parent"
          options={parentOptions}
          component={FormikSelectInput}
          isRequired
          isDisabled={isSubmitting || isLoading || disabledFields.includes('parent')}
          onChange={async (event: ChangeEvent<HTMLSelectElement>) => {
            const selectedValue = event.target.value as any as { label: string; value: string };
            if (selectedValue) {
              setFieldValue('parent', selectedValue);
              try {
                const data = await getOrganizationDetails({
                  alias: selectedValue.value,
                }).unwrap();
                if (data) {
                  setFieldValue('programTypes', data.programTypes || []);
                }
              } catch (error) {
                toast.error('Error fetching parent organization details');
                console.error('Error fetching parent organization details:', error);
              }
            } else {
              setFieldValue('parent', null);
            }
          }}
        />
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          gap={{ base: 'lg', desktop: 'xl' }}
        >
          <Field
            type="text"
            label="Company Phone Number"
            name="phoneNumber"
            id="phoneNumber"
            isRequired
            component={FormikTextInput}
            inputMask="phone"
            autoComplete="off"
            isDisabled={isSubmitting || isLoading || disabledFields.includes('phoneNumber')}
          />
          <Field
            type="text"
            label="Company Email"
            name="email"
            id="email"
            isRequired
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || isLoading || disabledFields.includes('email')}
          />
          <Field
            type="text"
            label="Federal Employer Id"
            name="federalEmployerId"
            id="federalEmployerId"
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || isLoading || disabledFields.includes('federalEmployerId')}
          />
        </Box>
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          gap={{ base: 'lg', desktop: 'xl' }}
        >
          <Field
            type="text"
            label="Insurance Company Name"
            name="insuranceCompanyName"
            id="insuranceCompanyName"
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || isLoading || disabledFields.includes('insuranceCompanyName')}
          />
          <Field
            type="text"
            label="Insurance Company Phone Number"
            name="insuranceCompanyPhoneNumber"
            id="insuranceCompanyPhoneNumber"
            component={FormikTextInput}
            inputMask="phone"
            autoComplete="off"
            isDisabled={isSubmitting || isLoading || disabledFields.includes('insuranceCompanyPhoneNumber')}
          />
        </Box>
        <AddressFormFields isSubmitting={isSubmitting} disabledFields={disabledFields} />
        <Box
          padding={{
            base: 'lg 0 0 0',
            tablet: 'lg lg 0 0',
          }}
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          childGap={{ base: 'lg', desktop: 'xl' }}
          width={{
            base: '100',
            tablet: '34',
          }}
        >
          <Field
            label="Status"
            name="status"
            id="status"
            options={statusOptions}
            component={FormikSelectInput}
            isDisabled={
              isSubmitting || isLoading || !isLightReachAdmin || disabledFields.includes('organizationStatus')
            }
            isRequired
            onChange={handleStatusChange}
          />
        </Box>
      </FormSection>
      {programTypesPermittedByParentOrgAndAnyCurrOrgProgramTypes.length > 1 && (
        <FormSection title="Program Types" description="Program types for this organization.">
          {programTypesPermittedByParentOrgAndAnyCurrOrgProgramTypes.map((programType: ProgramType) => (
            <Toggle
              key={programType}
              id={programType}
              label={ProgramTypeLabels[programType]}
              isChecked={values.programTypes?.includes(programType)}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                toggleProgramType(programType, event.target.checked);
              }}
              isDisabled={
                isSubmitting ||
                isLoading ||
                disabledFields.includes('programTypes') ||
                isNotPalmettoFinanceUserOrOrgAdmin
              }
            />
          ))}
        </FormSection>
      )}
      <FormSection title="Organization Types" description="Organization types for this organization.">
        {enabledOrganizationTypes.map((organizationType: OrganizationType) => (
          <Toggle
            key={organizationType}
            id={organizationType}
            label={OrganizationTypeLabels[organizationType]}
            isChecked={values.organizationTypes?.includes(organizationType)}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.checked ? [organizationType] : [OrganizationType.epc];
              setFieldValue('organizationTypes', value);
            }}
            isDisabled={
              isSubmitting ||
              isLoading ||
              disabledFields.includes('organizationTypes') ||
              !isPalmettoFinanceUser(claims)
            }
          />
        ))}
      </FormSection>
      <FormSection title="Authorized Signers" description="Authorized signers for this organization.">
        <FieldArray
          name="authorizedSigners"
          render={(arrayHelpers) => {
            return (
              <Box gap="md">
                {values.authorizedSigners?.map((_authorizedSigner: any, index: any) => (
                  <Box
                    key={index}
                    gap={{ base: 'md', desktop: 'xl' }}
                    direction="row"
                    alignItems="center"
                    borderWidth="xs"
                    borderColor="separator"
                    padding="md"
                    radius="md"
                  >
                    <Box
                      flex="auto"
                      gap={{ base: 'md', desktop: 'xl' }}
                      direction={{ base: 'column', tablet: 'row' }}
                    >
                      <Field
                        type="text"
                        label="Name"
                        name={`authorizedSigners[${index}].name`}
                        id={`authorizedSigners[${index}].name`}
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isAuthorizedSignerFieldDisabled()}
                      />
                      <Field
                        type="text"
                        label="Title"
                        name={`authorizedSigners[${index}].title`}
                        id={`authorizedSigners[${index}].title`}
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isAuthorizedSignerFieldDisabled()}
                      />
                      <Field
                        type="text"
                        label="Email"
                        name={`authorizedSigners[${index}].email`}
                        id={`authorizedSigners[${index}].email`}
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isAuthorizedSignerFieldDisabled()}
                      />
                    </Box>
                    {!isAuthorizedSignerFieldDisabled() && (
                      <Box
                        as="button"
                        type="button"
                        onClick={() => arrayHelpers.remove(index)}
                        aria-label="Remove authorized signer"
                        fontSize="lg"
                        borderWidth="0"
                        color="body-secondary"
                        padding="sm"
                        background="transparent"
                        hover={{
                          color: 'body-primary',
                        }}
                        cursor="pointer"
                      >
                        <Icon name="c-remove" />
                      </Box>
                    )}
                  </Box>
                ))}

                {!isAuthorizedSignerFieldDisabled() && (
                  <Box alignItems="flex-end">
                    <Button
                      as="button"
                      onClick={() => arrayHelpers.push({ name: '', title: '', email: '' })}
                      variant="secondary"
                      tone="neutral"
                      size="sm"
                      aria-label="Add Authorized Signer"
                      iconPrefix="add"
                    >
                      Authorized Signer
                    </Button>
                  </Box>
                )}
              </Box>
            );
          }}
        />
      </FormSection>
      {isLightReachAdmin &&
        values.programTypes.some((programType: ProgramType) => EXTENDED_WARRANTY_PROGRAM_TYPES.has(programType)) && (
          <FormSection
            title="Extended Warranty Dealer Information"
            description="Add your Dealer IDs so that LightReach can register all applicable extended warranties"
            headerChildren={
              <TextLink
                navigate={() => navigate(`/settings/organizations/${values.alias}/maintenance-settings`)}
                style={{ cursor: 'pointer' }}
              >
                Need to edit Maintenance Settings? Click here
              </TextLink>
            }
          >
            <FieldArray
              name="extendedWarrantyDealerInformation"
              render={(arrayHelpers) => {
                return (
                  <Box gap="md">
                    {values.extendedWarrantyDealerInformation &&
                      values.extendedWarrantyDealerInformation.length > 0 &&
                      values.extendedWarrantyDealerInformation.map((_extendedWarranty: any, index: any) => (
                        <Box
                          key={index}
                          gap={{ base: 'md', desktop: 'xl' }}
                          direction="row"
                          alignItems={{ base: 'center', tablet: 'flex-end' }}
                          borderWidth="xs"
                          borderColor="separator"
                          padding="md"
                          radius="md"
                        >
                          <Box flex="auto" gap={{ base: 'md', desktop: 'xl' }}>
                            <Box
                              flex="auto"
                              gap={{ base: 'md', desktop: 'xl' }}
                              direction={{ base: 'column', tablet: 'row' }}
                              alignItems={{ tablet: 'flex-end' }}
                            >
                              <SelectInputNative
                                label="Warranty Provider"
                                name={`extendedWarrantyDealerInformation[${index}].warrantyProvider`}
                                id={`extendedWarrantyDealerInformation[${index}].warrantyProvider`}
                                options={warrantyProviderOptions}
                                value={values.extendedWarrantyDealerInformation[index].warrantyProvider}
                                maxWidth="40"
                                onChange={(event: any) => {
                                  const selectedProvider = event.target.value;
                                  setFieldValue(
                                    `extendedWarrantyDealerInformation[${index}].warrantyProvider`,
                                    selectedProvider,
                                  );
                                  if (selectedProvider === WarrantyProvider.onPointWarranty) {
                                    setFieldValue(
                                      `extendedWarrantyDealerInformation[${index}].dealerLicenseNumber`,
                                      OnPointDealerID,
                                    );
                                    setFieldValue(
                                      `extendedWarrantyDealerInformation[${index}].contractSkuTermMappings`,
                                      defaultMapping ? [defaultMapping] : [],
                                    );
                                  } else {
                                    setFieldValue(
                                      `extendedWarrantyDealerInformation[${index}].dealerLicenseNumber`,
                                      '',
                                    );
                                    setFieldValue(
                                      `extendedWarrantyDealerInformation[${index}].contractSkuTermMappings`,
                                      [],
                                    );
                                  }
                                }}
                                isDisabled={isSubmitting || isLoading || !isLightReachAdmin}
                              />
                              <Field
                                type="text"
                                label="Dealer License Number"
                                name={`extendedWarrantyDealerInformation[${index}].dealerLicenseNumber`}
                                id={`extendedWarrantyDealerInformation[${index}].dealerLicenseNumber`}
                                component={FormikTextInput}
                                onChange={(event: any) =>
                                  setFieldValue(
                                    `extendedWarrantyDealerInformation[${index}].dealerLicenseNumber`,
                                    event.target.value,
                                  )
                                }
                                autoComplete="off"
                                maxWidth="40"
                                isRequired
                                isDisabled={isSubmitting || isLoading || !isLightReachAdmin}
                              />
                            </Box>
                            {values.extendedWarrantyDealerInformation[index].warrantyProvider ===
                              WarrantyProvider.onPointWarranty && (
                              <Box gap="sm" flex="auto">
                                {values.extendedWarrantyDealerInformation[index].contractSku && (
                                  <Box fontSize="xs" color="body-secondary">
                                    Legacy SKU: {values.extendedWarrantyDealerInformation[index].contractSku}
                                  </Box>
                                )}
                                <FieldArray
                                  name={`extendedWarrantyDealerInformation[${index}].contractSkuTermMappings`}
                                  render={(skuArrayHelpers) => {
                                    const mappings =
                                      values.extendedWarrantyDealerInformation[index].contractSkuTermMappings || [];

                                    return (
                                      <Box gap="sm">
                                        {mappings.map(
                                          (mapping: WarrantyContractSkuTermMapping, mappingIndex: number) => {
                                            const currentTerm = mapping.term ? mapping.term : undefined;
                                            const availableProgramOptions = getAvailableOnPointProgramOptions(
                                              mappings,
                                              programOptions,
                                              mappingIndex,
                                              activeContractSkus,
                                              mapping.programType,
                                              currentTerm,
                                            );
                                            const availableTerms = getAvailableOnPointContractSkuTerms(
                                              mappings,
                                              mapping.programType,
                                              mappingIndex,
                                              activeContractSkus,
                                              currentTerm,
                                            );
                                            const termOptions = availableTerms.map((term) => ({
                                              label: `${term} years`,
                                              value: String(term),
                                            }));
                                            const skuOptions = activeContractSkus
                                              .filter(
                                                (s) =>
                                                  s.warrantyProvider === WarrantyProvider.onPointWarranty &&
                                                  s.programType === mapping.programType &&
                                                  s.term === mapping.term,
                                              )
                                              .map((s) => ({
                                                label: s.displayName
                                                  ? `${s.contractSku} — ${s.displayName}`
                                                  : s.contractSku,
                                                value: s.contractSku,
                                              }));
                                            const mappingPath = `extendedWarrantyDealerInformation[${index}].contractSkuTermMappings[${mappingIndex}]`;

                                            return (
                                              <Box
                                                key={mappingIndex}
                                                gap="sm"
                                                direction={{ base: 'column', tablet: 'row' }}
                                                alignItems={{ tablet: 'flex-end' }}
                                              >
                                                <SelectInputNative
                                                  label="Program"
                                                  name={`${mappingPath}.programType`}
                                                  id={`${mappingPath}.programType`}
                                                  options={availableProgramOptions}
                                                  value={mapping.programType || ''}
                                                  maxWidth="24"
                                                  onChange={(event: any) => {
                                                    const programType = event.target.value as ProgramType;
                                                    const term = getAvailableOnPointContractSkuTerms(
                                                      mappings,
                                                      programType,
                                                      mappingIndex,
                                                      activeContractSkus,
                                                    )[0];
                                                    const contractSku = term
                                                      ? (activeContractSkus.find(
                                                          (s) =>
                                                            s.warrantyProvider ===
                                                              WarrantyProvider.onPointWarranty &&
                                                            s.programType === programType &&
                                                            s.term === term,
                                                        )?.contractSku ?? '')
                                                      : '';
                                                    setFieldValue(mappingPath, { programType, term, contractSku });
                                                  }}
                                                  isRequired
                                                  isDisabled={isSubmitting || isLoading || !isLightReachAdmin}
                                                />
                                                <SelectInputNative
                                                  label="Term"
                                                  name={`${mappingPath}.term`}
                                                  id={`${mappingPath}.term`}
                                                  options={termOptions}
                                                  value={mapping.term ? String(mapping.term) : ''}
                                                  width="70"
                                                  onChange={(event: any) => {
                                                    const term = Number(event.target.value);
                                                    const contractSku =
                                                      activeContractSkus.find(
                                                        (s) =>
                                                          s.warrantyProvider === WarrantyProvider.onPointWarranty &&
                                                          s.programType === mapping.programType &&
                                                          s.term === term,
                                                      )?.contractSku ?? '';
                                                    setFieldValue(`${mappingPath}.term`, term);
                                                    setFieldValue(`${mappingPath}.contractSku`, contractSku);
                                                  }}
                                                  isRequired
                                                  isDisabled={isSubmitting || isLoading || !isLightReachAdmin}
                                                />
                                                <SelectInputNative
                                                  label="Contract SKU"
                                                  name={`${mappingPath}.contractSku`}
                                                  id={`${mappingPath}.contractSku`}
                                                  options={skuOptions}
                                                  value={mapping.contractSku || ''}
                                                  maxWidth="40"
                                                  onChange={(event: any) =>
                                                    setFieldValue(`${mappingPath}.contractSku`, event.target.value)
                                                  }
                                                  isRequired
                                                  isDisabled={isSubmitting || isLoading || !isLightReachAdmin}
                                                />
                                                {isLightReachAdmin && (
                                                  <Box
                                                    as="button"
                                                    type="button"
                                                    onClick={() => skuArrayHelpers.remove(mappingIndex)}
                                                    aria-label="Remove Contract SKU Mapping"
                                                    fontSize="lg"
                                                    borderWidth="0"
                                                    color="body-secondary"
                                                    padding="sm"
                                                    background="transparent"
                                                    hover={{ color: 'body-primary' }}
                                                    cursor="pointer"
                                                  >
                                                    <Icon name="c-remove" />
                                                  </Box>
                                                )}
                                              </Box>
                                            );
                                          },
                                        )}
                                        {isLightReachAdmin &&
                                          getAvailableOnPointProgramOptions(
                                            mappings,
                                            programOptions,
                                            -1,
                                            activeContractSkus,
                                          ).length > 0 && (
                                            <Box alignItems="flex-start">
                                              <Button
                                                as="button"
                                                type="button"
                                                onClick={() => {
                                                  skuArrayHelpers.push({
                                                    programType: '',
                                                    term: undefined,
                                                    contractSku: '',
                                                  });
                                                }}
                                                variant="secondary"
                                                tone="neutral"
                                                size="sm"
                                                aria-label="Add Contract SKU Mapping"
                                                iconPrefix="add"
                                                isDisabled={isSubmitting || isLoading}
                                              >
                                                SKU Mapping
                                              </Button>
                                            </Box>
                                          )}
                                      </Box>
                                    );
                                  }}
                                />
                              </Box>
                            )}
                            <Toggle
                              id={`values.extendedWarrantyDealerInformation[${index}].isPreferredProvider`}
                              label="Preferred Provider"
                              isChecked={values.extendedWarrantyDealerInformation[index].isPreferredProvider}
                              isDisabled={
                                isSubmitting ||
                                isLoading ||
                                values.extendedWarrantyDealerInformation.length === 1 ||
                                !isLightReachAdmin
                              }
                              onChange={() => {
                                (values.extendedWarrantyDealerInformation as []).forEach(
                                  (_: any, elementIndex: number) => {
                                    if (index !== elementIndex) {
                                      setFieldValue(
                                        `extendedWarrantyDealerInformation[${elementIndex}].isPreferredProvider`,
                                        false,
                                      );
                                    } else {
                                      setFieldValue(
                                        `extendedWarrantyDealerInformation[${elementIndex}].isPreferredProvider`,
                                        !values.extendedWarrantyDealerInformation[elementIndex].isPreferredProvider,
                                      );
                                    }
                                  },
                                );
                              }}
                            />
                          </Box>
                          {isLightReachAdmin && (
                            <Box
                              as="button"
                              type="button"
                              onClick={() => {
                                const currentArray = values.extendedWarrantyDealerInformation;
                                const isRemovingPreferred = currentArray[index].isPreferredProvider;
                                const newArray = currentArray.filter((_: any, i: number) => i !== index);
                                // If removing the preferred provider, make the first remaining one preferred
                                if (isRemovingPreferred && newArray.length > 0) {
                                  newArray[0].isPreferredProvider = true;
                                }
                                setFieldValue('extendedWarrantyDealerInformation', newArray);
                              }}
                              aria-label="Remove Warranty Dealer Info"
                              fontSize="lg"
                              borderWidth="0"
                              color="body-secondary"
                              padding="sm"
                              background="transparent"
                              hover={{
                                color: 'body-primary',
                              }}
                              alignSelf="flex-start"
                              cursor="pointer"
                            >
                              <Icon name="trash" />
                            </Box>
                          )}
                        </Box>
                      ))}
                    {isLightReachAdmin && (
                      <Box alignItems="flex-end">
                        <Button
                          as="button"
                          onClick={() => {
                            arrayHelpers.push({
                              warrantyProvider: WarrantyProvider.onPointWarranty,
                              dealerLicenseNumber: OnPointDealerID,
                              isPreferredProvider: !values.extendedWarrantyDealerInformation?.length,
                              contractSkuTermMappings: defaultMapping ? [defaultMapping] : [],
                            });
                          }}
                          variant="secondary"
                          tone="neutral"
                          size="sm"
                          aria-label="Add Additional Warranty Information"
                          iconPrefix="add"
                        >
                          Warranty Info
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              }}
            />
          </FormSection>
        )}
      {isLightReachAdmin && accountBundling && (
        <FormSection
          title="Account Bundling"
          description="Controls whether EPC users in this organization and its sub-organizations can bundle accounts in the Portal."
        >
          <Toggle
            id="accountBundlingEnabled"
            label="Account Bundling Enabled"
            isChecked={!!bundlingInheritedFrom || !!values.accountBundlingEnabled}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setFieldValue('accountBundlingEnabled', event.target.checked);
            }}
            isDisabled={isSubmitting || isLoading || !!bundlingInheritedFrom}
            helpText={
              bundlingInheritedFrom
                ? `Inherited from ${bundlingInheritedFrom.name}. Disable it there to change this setting.`
                : undefined
            }
          />
        </FormSection>
      )}
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        alignItems={{
          base: 'stretch',
          tablet: 'flex-end',
        }}
        justifyContent={{
          tablet: 'flex-end',
        }}
        childGap="sm"
        style={{ flexShrink: 0 }}
        padding="lg 0 0 0"
      >
        <Button
          as="a"
          navigate={() => navigate(cancelButtonUrl)}
          variant="secondary"
          tone="neutral"
          size="md"
          isDisabled={isLoading}
          isLoading={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          type="submit"
          isDisabled={isLoading || !allowsAction || disableSubmit}
          isLoading={isSubmitting}
        >
          Submit
        </Button>
      </Box>
      {pendingStatusChange && (
        <ChangeOrganizationStatusModal
          isOpen={!!pendingStatusChange}
          fromStatus={pendingStatusChange.from}
          toStatus={pendingStatusChange.to}
          onCancel={handleStatusModalCancel}
          onConfirm={handleStatusModalConfirm}
        />
      )}
    </>
  );
};
